import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, MessageCircle, Phone } from "lucide-react";
import * as Icons from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import FloatingActions from "../components/FloatingActions";
import { SERVICES, SERVICE_DETAILS, PHONE, PHONE_DISPLAY, WHATSAPP_MSG } from "../lib/data";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.id === slug);
  const detail = SERVICE_DETAILS[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!service || !detail) return <Navigate to="/" replace />;

  const Icon = Icons[service.icon] || Icons.Sparkles;
  const otherServices = SERVICES.filter((s) => s.id !== slug).slice(0, 3);

  return (
    <div data-testid={`service-page-${slug}`} className="min-h-screen bg-black">
      <Navbar />
      <FloatingActions />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/10 pt-32 md:pt-40">
        <div className="pointer-events-none absolute left-1/2 top-16 h-[400px] w-[700px] -translate-x-1/2 rounded-full bg-white/[0.05] blur-[150px]" />
        <div className="relative mx-auto max-w-[90rem] px-6 pb-20 md:px-12 md:pb-28">
          <Link
            to="/#services"
            data-testid="service-back"
            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-white/65 hover:text-white transition-colors"
          >
            <ArrowLeft size={14} /> All services
          </Link>

          <div className="mt-10 grid gap-10 md:grid-cols-12 md:items-end">
            <div className="md:col-span-8">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-1.5 font-mono text-[0.65rem] uppercase tracking-[0.2em] text-white/70">
                <Icon size={12} strokeWidth={1.75} /> {detail.label}
              </span>
              <h1 className="mt-6 max-w-3xl font-display text-4xl font-extrabold leading-[0.9] tracking-tighter text-white md:text-7xl">
                {service.title}.
              </h1>
              <p className="mt-6 max-w-2xl text-lg text-white/70 leading-relaxed md:text-xl">
                <span className="font-serif-emph text-white">{detail.tagline}</span>
                <br className="hidden md:block" />
                {detail.intro}
              </p>
            </div>
            <div className="md:col-span-4">
              <Link
                to="/booking"
                data-testid="service-book-btn"
                className="group inline-flex w-full items-center justify-between gap-2 rounded-2xl bg-white px-6 py-4 font-semibold text-black transition-colors hover:bg-white/90"
              >
                Book strategy call
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <a
                  href={`https://wa.me/${PHONE.replace("+", "")}?text=${WHATSAPP_MSG}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="service-whatsapp"
                  className="flex items-center justify-center gap-2 rounded-2xl border border-white/15 px-4 py-3 text-sm text-white transition-colors hover:bg-white/5"
                >
                  <MessageCircle size={16} /> WhatsApp
                </a>
                <a
                  href={`tel:${PHONE}`}
                  data-testid="service-call"
                  className="flex items-center justify-center gap-2 rounded-2xl border border-white/15 px-4 py-3 text-sm text-white transition-colors hover:bg-white/5"
                >
                  <Phone size={16} /> Call
                </a>
              </div>
            </div>
          </div>

          {/* framed hero image */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
            className="mt-16 overflow-hidden rounded-3xl border border-white/10"
          >
            <div className="relative aspect-[21/9] w-full">
              <img
                src={service.image}
                alt={service.title}
                className="h-full w-full object-cover grayscale contrast-125 opacity-90"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-black/40" />
              <div className="absolute inset-0 grain" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* WHAT WE DO */}
      <section className="border-b border-white/10 py-24 md:py-32">
        <div className="mx-auto max-w-[90rem] px-6 md:px-12">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-white/65">
            / What we do
          </p>
          <h2 className="mt-4 max-w-2xl font-display text-3xl font-extrabold tracking-tighter text-white md:text-5xl">
            Inside the engagement.
          </h2>

          <div className="mt-14 grid gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/10 md:grid-cols-2">
            {detail.what.map((w, i) => (
              <motion.div
                key={w.h}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                data-testid={`what-${i}`}
                className="bg-black p-8 md:p-10"
              >
                <span className="font-mono text-xs text-white/75">
                  0{i + 1}
                </span>
                <h3 className="mt-4 font-display text-2xl font-bold tracking-tight text-white">
                  {w.h}
                </h3>
                <p
                  className="mt-3 text-sm leading-relaxed text-white/75"
                  dangerouslySetInnerHTML={{ __html: w.p }}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* DELIVERABLES + KPIS */}
      <section className="border-b border-white/10 py-24 md:py-32">
        <div className="mx-auto max-w-[90rem] px-6 md:px-12">
          <div className="grid gap-12 md:grid-cols-12">
            <div className="md:col-span-6">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-white/65">
                / Deliverables
              </p>
              <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tighter text-white md:text-5xl">
                What you receive.
              </h2>
              <ul className="mt-10 space-y-4">
                {detail.deliverables.map((d, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.04 }}
                    className="flex items-start gap-3 border-b border-white/10 pb-4 text-white"
                  >
                    <Check size={18} className="mt-0.5 shrink-0 text-white/70" strokeWidth={2} />
                    <span dangerouslySetInnerHTML={{ __html: d }} />
                  </motion.li>
                ))}
              </ul>
            </div>

            <div className="md:col-span-5 md:col-start-8">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-white/65">
                / Metrics we move
              </p>
              <h3 className="mt-4 font-display text-2xl font-extrabold tracking-tighter text-white md:text-3xl">
                We report on what matters.
              </h3>
              <div className="mt-8 flex flex-wrap gap-3">
                {detail.kpis.map((k) => (
                  <span
                    key={k}
                    className="rounded-full border border-white/15 px-4 py-2 font-mono text-xs uppercase tracking-wider text-white/80"
                    dangerouslySetInnerHTML={{ __html: k }}
                  />
                ))}
              </div>

              <div className="mt-10 rounded-2xl border border-white/10 p-8">
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-white/65">
                  / Built for
                </p>
                <p className="mt-3 text-white/80 leading-relaxed">{detail.for}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-b border-white/10 py-24 md:py-32">
        <div className="mx-auto max-w-[90rem] px-6 md:px-12">
          <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-10 md:p-16">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-white/65">
              / Next step
            </p>
            <h2 className="mt-4 max-w-3xl font-display text-4xl font-extrabold leading-[0.95] tracking-tighter text-white md:text-6xl">
              Ready to see what we’d unlock for you?
            </h2>
            <p className="mt-6 max-w-xl text-white/70 leading-relaxed">
              Book a 30-minute strategy call. We’ll audit your funnel live and share
              two or three moves you can make this week — free, whether you hire us or not.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/booking"
                data-testid="service-cta-book"
                className="group inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 font-semibold text-black transition-colors hover:bg-white/90"
              >
                Book free audit
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <a
                href={`https://wa.me/${PHONE.replace("+", "")}?text=${WHATSAPP_MSG}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-white/20 px-8 py-4 font-semibold text-white transition-colors hover:bg-white/5"
              >
                <MessageCircle size={18} /> WhatsApp us
              </a>
              <a href={`tel:${PHONE}`} className="hidden items-center gap-2 rounded-full border border-white/20 px-8 py-4 font-semibold text-white transition-colors hover:bg-white/5 sm:inline-flex">
                <Phone size={18} /> {PHONE_DISPLAY}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Related services */}
      <section className="py-24 md:py-32">
        <div className="mx-auto max-w-[90rem] px-6 md:px-12">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-white/65">
            / Other services
          </p>
          <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tighter text-white md:text-5xl">
            Keep exploring.
          </h2>
          <div className="mt-10 grid gap-4 md:grid-cols-3">
            {otherServices.map((s) => (
              <Link
                key={s.id}
                to={`/services/${s.id}`}
                data-testid={`related-${s.id}`}
                className="group rounded-2xl border border-white/10 bg-white/[0.02] p-6 transition-colors hover:border-white/40"
              >
                <span className="font-mono text-[0.65rem] uppercase tracking-[0.2em] text-white/65">
                  {s.category}
                </span>
                <div className="mt-6 flex items-center justify-between">
                  <h3 className="font-display text-xl font-bold tracking-tight text-white">
                    {s.title}
                  </h3>
                  <ArrowRight size={18} className="text-white/75 transition-transform group-hover:translate-x-1 group-hover:text-white" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
